import * as React from 'react';

import {
    StyledButtons,
    StyledContent,
    StyledLayout,
    StyledMedia,
    StyledTextElements
} from './styles';
import {CounterRow} from './counterRow';
import styled from 'styled-components';
import {makeClip} from '../helpers';
import {useExpiryData} from './useExpiryData';
import {useLiveRegion} from './useLiveRegion';

const StyledHiddenLiveRegion = styled.span`
    ${makeClip};
`;

export type RemainingTime = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
};
export interface TimeConversions {
    counter: {
        label: string;
        time: string[];
    }[];
    dateTime: string;
    countdownString?: string;
}
type ConvertTimeParams = {
    milliseconds: number;
    dayLabel: string;
    hourLabel: string;
    minutesLabel: string;
    secondsLabel: string;
    isFinalDay: boolean;
};

export const getTime = (milliseconds: number): RemainingTime => {
    // https://www.w3schools.com/howto/howto_js_countdown.asp
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
        (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    return {days, hours, minutes, seconds};
};

export const convertTime = ({
    milliseconds,
    dayLabel,
    hourLabel,
    minutesLabel,
    secondsLabel,
    isFinalDay
}: ConvertTimeParams): TimeConversions => {
    const {days, hours, minutes, seconds} = getTime(milliseconds);
    const counterArr = [
        {label: dayLabel, time: days},
        {label: hourLabel, time: hours},
        {label: minutesLabel, time: minutes},
        {label: secondsLabel, time: seconds}
    ];
    const counter = counterArr.map(({label, time}) => ({
        label,
        time: time.toString().padStart(2, '0').split('')
    }));
    const dateTime = `PT${days}D${hours}H${minutes}M${seconds}S`;

    const daysInfo = isFinalDay ? '' : `${days} ${dayLabel}`;
    const secondsInfo = isFinalDay ? `${seconds} ${secondsLabel}` : '';

    const countdownString =
        `${daysInfo} ${hours} ${hourLabel} ${minutes} ${minutesLabel} ${secondsInfo}`
            .trimStart()
            .trim();

    return {counter, dateTime, countdownString};
};

export const ON_TIMER_END_EVENT = 'ontimerend';

export interface CountdownItemProps {
    media: React.ReactElement;
    heading: React.ReactElement;
    copy: React.ReactElement;
    bullets?: React.ReactElement;
    buttons?: React.ReactElement;
    disclaimers: React.ReactElement;
    isCutout: boolean;
    dayLabel: string;
    hourLabel: string;
    minutesLabel: string;
    secondsLabel: string;
    startOfReveal: number;
    timeDistance: number;
}
function countedTimeDistance(startOfReveal: number) {
    return startOfReveal - Date.now();
}

export const CountdownItem = (props: CountdownItemProps) => {
    const {
        media,
        heading,
        copy,
        bullets,
        buttons,
        disclaimers,
        isCutout,
        dayLabel,
        hourLabel,
        minutesLabel,
        secondsLabel,
        startOfReveal
    } = props;

    const [timeDistanceState, setTimeDistanceState] = React.useState<number>(
        countedTimeDistance(startOfReveal)
    );

    const {isFinalDay, isCountdownActive, hasCountdownJustExpired} =
        useExpiryData({
            timeDistanceState
        });

    const {counter, dateTime, countdownString} = convertTime({
        milliseconds: timeDistanceState,
        dayLabel,
        hourLabel,
        minutesLabel,
        secondsLabel,
        isFinalDay
    });

    const ref = React.useRef<HTMLDivElement>(null);

    const initialCountdownStringRef = React.useRef(countdownString);
    const initialTimeDistanceRef = React.useRef(timeDistanceState);

    React.useEffect(() => {
        if (timeDistanceState <= 0) return;

        const timeout = setTimeout(() => {
            setTimeDistanceState(countedTimeDistance(startOfReveal));
        }, 1000);

        return () => clearTimeout(timeout);
    }, [timeDistanceState, startOfReveal]);

    React.useEffect(() => {
        if (timeDistanceState <= 0) {
            ref.current?.dispatchEvent(
                new CustomEvent(ON_TIMER_END_EVENT, {bubbles: true})
            );
            return;
        }
    }, [timeDistanceState]);

    const liveRegionContent = useLiveRegion({
        isCountdownActive,
        hasCountdownJustExpired,
        initialCountdownStringRef,
        initialTimeDistanceRef
    });

    return (
        <StyledLayout ref={ref}>
            {/* MDN suggests to add both role and aria-live assertive to maximize compatibility */}
            <StyledHiddenLiveRegion
                role="alert"
                aria-live="assertive"
                aria-relevant="additions text"
            >
                {liveRegionContent}
            </StyledHiddenLiveRegion>
            <StyledMedia isCutout={isCutout}>{media}</StyledMedia>
            <StyledContent>
                <StyledTextElements>
                    {heading}
                    {copy}
                    {bullets}
                </StyledTextElements>
                {buttons ? <StyledButtons>{buttons}</StyledButtons> : null}
                <CounterRow
                    counter={counter}
                    dateTime={dateTime}
                    isFinalDay={isFinalDay}
                    countdownString={isCountdownActive ? countdownString : ''}
                />
            </StyledContent>
            {disclaimers}
        </StyledLayout>
    );
};
